import { COLORS } from '../../constants/theme';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { IntelFreshnessIndicator } from './IntelFreshnessIndicator';
import {
  computeGainMod,
  computeMassacreMod,
  computeTraditionalMarchGain,
  computeConquestGain,
  computePlunderGain,
  computeRazeGain,
  computeLearnGain,
  computeMassacreGain,
} from '../../utils/estimatedGain';
import type { Outcome } from './WarRoomOutcome';
import type { AttackType } from '../../types/attack';
import type { KingdomMember } from '../../types/magic';
import type { TargetIntelData } from '../../hooks/useTargetIntel';

const LEARN_VISIBLE_CAP = 3;

interface EstimatedGainPanelProps {
  selectedAttackType: AttackType;
  verdict: Outcome;
  targetMember: KingdomMember | null;
  ownNetworth: number;
  ownAcres: number;
  attackOP: number;
  targetTD: number | null;
  militaryEffectPercentage: number;
  isOwnKingdomAtWar: boolean;
  ownStance: string;
  targetStance: string;
  targetDragonType: string | null;
  intel: TargetIntelData;
}

/**
 * SF-174 Component 6: attack-type-conditional Estimated Gain panel, computed entirely client-side
 * from already-fetched intel — see `estimatedGain.ts` for the formulas and their documented
 * approximations. Gated on the page's own already-computed predicted verdict: `CombatSystem.java`
 * only computes attack gains `if (attackerWins)`, so this panel shows nothing but a "win the
 * fight" line for a predicted Lose/Unknown, rather than a number for an attack the rest of the
 * page simultaneously labels a loss.
 */
export function EstimatedGainPanel({
  selectedAttackType,
  verdict,
  targetMember,
  ownNetworth,
  ownAcres,
  attackOP,
  targetTD,
  militaryEffectPercentage,
  isOwnKingdomAtWar,
  ownStance,
  targetStance,
  targetDragonType,
  intel,
}: EstimatedGainPanelProps) {
  return (
    <div
      className="rounded p-3 space-y-2"
      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
    >
      <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
        Estimated Gain
      </div>

      {verdict !== 'Win' || !targetMember ? (
        <div className="text-sm" style={{ color: COLORS.text.muted }}>
          Win the fight to see estimated gains
        </div>
      ) : (
        <PanelBody
          selectedAttackType={selectedAttackType}
          targetMember={targetMember}
          ownNetworth={ownNetworth}
          ownAcres={ownAcres}
          attackOP={attackOP}
          targetTD={targetTD}
          militaryEffectPercentage={militaryEffectPercentage}
          isOwnKingdomAtWar={isOwnKingdomAtWar}
          ownStance={ownStance}
          targetStance={targetStance}
          targetDragonType={targetDragonType}
          intel={intel}
        />
      )}
    </div>
  );
}

function PanelBody({
  selectedAttackType,
  targetMember,
  ownNetworth,
  ownAcres,
  attackOP,
  targetTD,
  militaryEffectPercentage,
  isOwnKingdomAtWar,
  ownStance,
  targetStance,
  targetDragonType,
  intel,
}: Omit<EstimatedGainPanelProps, 'verdict'> & { targetMember: KingdomMember }) {
  const gainMod = computeGainMod({
    targetNetworth: targetMember.networth,
    ownNetworth,
    militaryEffectPercentage,
    isOwnKingdomAtWar,
    ownStance,
    targetStance,
    targetDragonType,
  });

  switch (selectedAttackType) {
    case 'TRADITIONAL_MARCH': {
      const { acresCaptured } = computeTraditionalMarchGain(gainMod, targetMember.land, ownAcres);
      return <StatLine label="Acres captured" value={`~${formatNumber(acresCaptured)}`} />;
    }
    case 'CONQUEST': {
      const { acresCaptured } = computeConquestGain(attackOP, targetTD ?? 0, targetMember.land, ownAcres);
      return <StatLine label="Acres captured" value={`~${formatNumber(acresCaptured)}`} />;
    }
    case 'RAZE': {
      const { acresRazed } = computeRazeGain(gainMod, targetMember.land, ownAcres);
      return <StatLine label="Acres razed" value={`~${formatNumber(acresRazed)}`} />;
    }
    case 'PLUNDER': {
      const gain = computePlunderGain(gainMod, intel.cbData);
      if (!gain) {
        return <MissingIntelPrompt source="CB" label="Crystal Ball" report={intel.cbReport} />;
      }
      return (
        <div className="space-y-1">
          {intel.cbReport && <IntelFreshnessIndicator capturedAt={intel.cbReport.capturedAt} label="Crystal Ball" />}
          <StatLine label="Gold" value={`~${formatNumber(gain.gold)}`} />
          <StatLine label="Runes" value={`~${formatNumber(gain.runes)}`} />
          <StatLine label="Food" value={`~${formatNumber(gain.food)}`} />
        </div>
      );
    }
    case 'LEARN': {
      const gain = computeLearnGain(gainMod, intel.sosData);
      if (!gain) {
        return <MissingIntelPrompt source="SOS" label="Spy on Sciences" report={intel.sosReport} />;
      }
      const visible = gain.categories.slice(0, LEARN_VISIBLE_CAP);
      const remaining = gain.categories.length - visible.length;
      return (
        <div className="space-y-1">
          {intel.sosReport && <IntelFreshnessIndicator capturedAt={intel.sosReport.capturedAt} label="Spy on Sciences" />}
          {visible.map((c) => (
            <StatLine key={c.scienceType} label={formatEnumLabel(c.scienceType)} value={`~${formatNumber(c.stolen)}`} />
          ))}
          {remaining > 0 && (
            <div className="text-xs" style={{ color: COLORS.text.muted }}>+{remaining} more</div>
          )}
          <StatLine label="Total books stolen" value={`~${formatNumber(gain.total)}`} />
        </div>
      );
    }
    case 'MASSACRE': {
      const massacreMod = computeMassacreMod({ ownStance, targetDragonType });
      const gain = computeMassacreGain(massacreMod, intel.cbData, intel.sotData);
      if (!gain) {
        const missingCb = !intel.cbData;
        return (
          <MissingIntelPrompt
            source={missingCb ? 'CB' : 'SOT'}
            label={missingCb ? 'Crystal Ball' : 'Spy on Throne'}
            report={missingCb ? intel.cbReport : intel.sotReport}
          />
        );
      }
      return (
        <div className="space-y-1">
          {intel.cbReport && <IntelFreshnessIndicator capturedAt={intel.cbReport.capturedAt} label="Crystal Ball" />}
          {intel.sotReport && <IntelFreshnessIndicator capturedAt={intel.sotReport.capturedAt} label="Spy on Throne" />}
          <StatLine label="Peasants killed" value={`~${formatNumber(gain.peasantsKilled)}`} />
          <StatLine label="Thieves killed" value={`~${formatNumber(gain.thievesKilled)}`} />
          <StatLine label="Wizards killed" value={`~${formatNumber(gain.wizardsKilled)}`} />
        </div>
      );
    }
    default:
      return null;
  }
}

function StatLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span style={{ color: COLORS.text.tertiary }}>{label}</span>
      <span style={{ color: COLORS.text.primary }}>{value}</span>
    </div>
  );
}

function MissingIntelPrompt({
  label,
}: {
  source: string;
  label: string;
  report: { capturedAt: string } | null;
}) {
  return (
    <div
      className="text-xs rounded px-2 py-1"
      style={{ backgroundColor: '#2a0a0a', border: '1px solid #4a1a1a', color: COLORS.negative }}
    >
      No {label} intel. Use Quick Intel above to run {label}.
    </div>
  );
}
